import {FormData} from './classes/data';
import {FormSchema} from './classes/schema';
import {
  FormSchemaValidations,
  FormSchemaValidationMessages
} from './form.validators';
import {FormSchemaMask} from './form.mask';

import {IconMode} from '../icon/icon.component';

export interface FormSettings {
  groups: Array<FormSchemaGroupData>;
  schema: Array<FormSchemaData>;
}

export interface FormSchemaGroupData {
  key: string;
  label?: string;
  icon?: string;
  description?: string;
  iconMode?: IconMode;
}

export interface FormSchemaGroup {
  key: string;
  label: string;
  icon: string;
  iconMode: string;
  description?: string;
  schema: Array<FormSchemaData>;
}

// Generic schema data
export interface BaseFormSchemaData<EventType = FormSchemaEvent> {
  key?: string;
  type?: FormSchemaTypes;
  group?: string;
  label?: string | FormSchemaStringFunction;
  helper?: HelperSchema;
  grid?: FormGrid;
  css_class?: string | FormSchemaStringFunction;
  autofocus?: boolean;
  disabled?: boolean | FormSchemaBooleanFunction;
  validations?: FormSchemaValidations;
  validation_messages?: FormSchemaValidationMessages;
  events?: FormSchemaEvents<EventType>;
  value?: any;
  default_value?: any;
}

// Schema types

export type FormSchemaTypes =
  | FormTextSchemaTypes
  | FormTextareaSchemaTypes
  | FormNumberSchemaTypes
  | FormColorSchemaTypes
  | FormDateTimeSchemaTypes
  | FormFileSchemaTypes
  | FormRadioSchemaTypes
  | FormBooleanSchemaTypes
  | FormSelectSchemaTypes
  | FormTypeSearchSchemaTypes
  | FormWysiwygSchemaTypes;

export type FormTextSchemaTypes = 'text' | 'email' | 'password' | 'tel' | 'url';
export type FormTextareaSchemaTypes = 'textarea' | 'texteditor';
export type FormNumberSchemaTypes = 'number' | 'range';
export type FormColorSchemaTypes = 'color';
export type FormDateTimeSchemaTypes = 'date' | 'time';
export type FormFileSchemaTypes = 'file';
export type FormRadioSchemaTypes = 'radio';
export type FormBooleanSchemaTypes = 'checkbox' | 'toggle';
export type FormSelectSchemaTypes = 'select';
export type FormTypeSearchSchemaTypes = 'typeahead';
export type FormWysiwygSchemaTypes = 'wysiwyg';

export type FormSchemaDataWithActionButton =
  | FormTextSchemaData
  | FormNumberSchemaData
  | FormDateTimeSchemaData
  | FormSelectSchemaData
  | FormTypeSearchSchemaData;

export type FormSchemaDataWithIcon =
  | FormTextSchemaData
  | FormNumberSchemaData
  | FormDateTimeSchemaData
  | FormSelectSchemaData
  | FormTypeSearchSchemaData;

export interface FormTextSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type?: FormTextSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  mask?: FormSchemaMask;
  spinner?: boolean | FormSchemaBooleanFunction;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: string;
  default_value?: string;
}

export interface FormTextareaSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormTextareaSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  grid?: FormGridWithRows;
  value?: string;
}

export interface FormNumberSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormNumberSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  step?: string | number;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: number;
  default_value?: number;
}

export interface FormColorSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormColorSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: string;
  default_value?: string;
}

export interface FormDateTimeSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormDateTimeSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: string | Date;
  default_value?: string | Date;
}

export interface FormFileSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormFileSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  action_button?: FormSchemaActionButton;
  allow_camera?: boolean;
  allowNotCropAction?: boolean;
  cropBoxResizable?: boolean;
  aspectRatio?: {
    width: number;
    height: number;
  };
  minWidth?: number;
  minHeight?: number;
  zoomable?: boolean;
  icon?: FormSchemaIcon;
  value?: string;
  default_value?: string;
}

export interface FormRadioSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: 'radio';
  options?: Array<any>;
  options_label_format?: (item: any) => string;
  options_label?: string;
  options_value_format?: (item: any) => string;
  options_value?: string;
  value?: any;
  default_value?: any;
}

export interface FormBooleanSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormBooleanSchemaTypes;
  color?: string;
  value?: boolean;
  default_value?: boolean;
}

export interface FormSelectSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormSelectSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  multiple?: boolean;
  options?: Array<any>;
  options_label_format?: (item: any) => string;
  options_label?: string;
  options_value_format?: (item: any) => string;
  options_value?: string;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: any;
  default_value?: any;
}

export interface FormSelectSchemaFormattedOptions {
  label: string;
  value: any;
  checked?: boolean;
}

export interface FormTypeSearchSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type: FormTypeSearchSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  match_only?: boolean;
  debounce?: number;
  search: FormSchemaTypeSearch;
  search_list_accessor?: string;
  search_list_format?: (item: any) => string;
  search_value_accessor?: string;
  search_value_format?: (item: any) => any;
  search_input_accessor?: string;
  search_input_format?: (item: any) => string;
  search_list?: Array<any>;
  search_empty_text?: string | FormSchemaStringFunction;
  search_on_focus?: boolean;
  select_on_focus?: boolean;
  always_show_list?: boolean;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: any;
  default_value?: any;
}

export interface FormWysiwygSchemaData<EventType = FormSchemaEvent>
  extends BaseFormSchemaData<EventType> {
  type?: FormWysiwygSchemaTypes;
  placeholder?: string | FormSchemaStringFunction;
  mask?: FormSchemaMask;
  spinner?: boolean | FormSchemaBooleanFunction;
  toolbar?: ToolbarQuill;
  action_button?: FormSchemaActionButton;
  icon?: FormSchemaIcon;
  value?: string;
  default_value?: string;
  group: string;
}

export type FormSchemaData<EventType = FormSchemaEvent> =
  | FormTextSchemaData<EventType>
  | FormTextareaSchemaData<EventType>
  | FormNumberSchemaData<EventType>
  | FormColorSchemaData<EventType>
  | FormDateTimeSchemaData<EventType>
  | FormFileSchemaData<EventType>
  | FormRadioSchemaData<EventType>
  | FormBooleanSchemaData<EventType>
  | FormSelectSchemaData<EventType>
  | FormTypeSearchSchemaData<EventType>
  | FormWysiwygSchemaData<EventType>;

// Schema properties

export type FormSchemaTypeSearch = (
  value: string,
  callback: (response: Array<any>) => void
) => void;

export interface ToolbarQuill {
  container: Array<string>;
  handlers?: { [key: string]: () => void };
}

export interface AddSchemaOptions {
  before?: string;
  after?: string;
}

export interface FormValues {
  [key: string]: any;
}

export interface FormSchemaActionButton {
  icon?: string;
  icon_mode?: string;
  label?: string | FormSchemaEventFunction<FormSchemaEvent, string>;
  title?: string | FormSchemaEventFunction<FormSchemaEvent, string>;
  onClick: FormSchemaEventFunction<FormSchemaEvent, void>;
  disabled?: boolean | FormSchemaEventFunction<FormSchemaEvent, boolean>;
  visible?: boolean | FormSchemaEventFunction<FormSchemaEvent, boolean>;
  mode?: string;
  color?: string;
}

export interface FormSchemaIcon {
  name: string;
  mode?: IconMode;
  size?: string;
  color?: string;
  highlight_state?: boolean;
  visible?: boolean | FormSchemaEventFunction<FormSchemaEvent, boolean>;
}

export interface FormGrid {
  cols?: number | FormGridCols;
  offsets?: number | FormGridOffsets;
}

export interface FormGridWithRows extends FormGrid {
  rows?: number;
}

export interface FormGridCols {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export interface FormGridOffsets extends FormGridCols {
}

export type FormSubmitHandler = (
  event: FormSubmitEvent,
  callback: () => void
) => void;

export interface FormSubmitEvent {
  data: any;
  valid: boolean;
  errors: any;
}

export interface FormSchemaEvent {
  eventRef: any;
  form: FormData;
  schema: FormSchema;
}

export interface FormSchemaEvents<EventType = FormSchemaEvent> {
  onInit?: FormSchemaEventFunction<EventType>;
  onKeydown?: FormSchemaEventFunction<EventType>;
  onKeyup?: FormSchemaEventFunction<EventType>;
  onKeypress?: FormSchemaEventFunction<EventType>;
  onChange?: FormSchemaEventFunction<EventType>;
  onFocus?: FormSchemaEventFunction<EventType>;
  onBlur?: FormSchemaEventFunction<EventType>;
}

export type FormSchemaEventFunction<
  EventType = FormSchemaEvent,
  ResponseType = void
> = (event: EventType) => ResponseType;

export type FormSchemaStringFunction = (
  schema: FormSchema,
  form: FormData
) => string;

export interface HelperSchema {
  icon?: string,
  action: () => void
}

export type FormSchemaBooleanFunction = (
  schema: FormSchema,
  form: FormData
) => boolean;
